import React, { useState, useRef } from 'react';

const BackupCodeEntry = ({ onVerified, onSwitchToEmail }) => {
  const [codeInputs, setCodeInputs] = useState(['', '', '', '', '', '']);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const inputRefs = useRef([]);

  const handleCodeInput = (index, value) => {
    if (!/^\d?$/?.test(value)) return;
    const updated = [...codeInputs];
    updated[index] = value;
    setCodeInputs(updated);
    setError('');
    if (value && index < 5) {
      inputRefs?.current?.[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e?.key === 'Backspace' && !codeInputs?.[index] && index > 0) {
      inputRefs?.current?.[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e?.preventDefault();
    const pasted = e?.clipboardData?.getData('text')?.replace(/\D/g, '')?.slice(0, 6);
    if (pasted?.length > 0) {
      const updated = ['', '', '', '', '', ''];
      pasted?.split('')?.forEach((char, i) => { updated[i] = char; });
      setCodeInputs(updated);
      const nextFocus = Math.min(pasted?.length, 5);
      inputRefs?.current?.[nextFocus]?.focus();
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    const code = codeInputs?.join('');
    if (code?.length < 6) { setError('Skriv inn alle 6 sifrene.'); return; }
    setIsLoading(true);
    await new Promise(r => setTimeout(r, 600));
    // Validate against stored SMS code or backup codes
    const twoFASettings = JSON.parse(localStorage.getItem('2fa_settings') || '{}');
    const backupCodes = twoFASettings?.backupCodes || [];
    // For demo: accept any 6-digit code if 2FA is enabled, or check backup codes
    const isValid = twoFASettings?.enabled && (backupCodes?.some(bc => bc?.replace('-', '') === code) || code === '123456');
    setIsLoading(false);
    if (!isValid) {
      setError('Ugyldig sikkerhetskode. Sjekk koden og prøv igjen.');
      setCodeInputs(['', '', '', '', '', '']);
      inputRefs?.current?.[0]?.focus();
      return;
    }
    setError('');
    onVerified();
  };

  return (
    <div>
      <div style={{ width: '48px', height: '3px', background: 'var(--color-accent)', marginBottom: '24px' }} />
      <h2 style={{ fontFamily: 'var(--font-heading)', fontSize: '28px', fontWeight: 700, color: 'var(--color-foreground)', marginBottom: '8px', lineHeight: 1.2 }}>
        Sikkerhetskode
      </h2>
      <p style={{ fontFamily: 'var(--font-body)', fontSize: '14px', fontWeight: 400, color: 'var(--color-muted-foreground)', marginBottom: '32px', lineHeight: 1.6 }}>
        Skriv inn en av sikkerhetskodene du fikk da du aktiverte 2FA.
      </p>
      <form onSubmit={handleSubmit} noValidate>
        <div className="mb-6">
          <label style={{ fontFamily: 'var(--font-caption)', fontSize: '11px', fontWeight: 500, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--color-muted-foreground)', display: 'block', marginBottom: '16px' }}>
            6-sifret sikkerhetskode
          </label>
          <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-start' }} onPaste={handlePaste}>
            {codeInputs?.map((digit, index) => (
              <input
                key={index}
                ref={el => inputRefs.current[index] = el}
                type="text"
                inputMode="numeric"
                maxLength={1}
                value={digit}
                onChange={e => handleCodeInput(index, e?.target?.value)}
                onKeyDown={e => handleKeyDown(index, e)}
                style={{
                  width: '48px',
                  height: '56px',
                  textAlign: 'center',
                  background: 'rgba(255,255,255,0.04)',
                  border: `2px solid ${error ? '#FF5722' : digit ? 'var(--color-accent)' : 'rgba(156, 163, 175, 0.3)'}`,
                  borderRadius: 'var(--radius-sm)',
                  fontFamily: 'var(--font-data)',
                  fontSize: '20px',
                  fontWeight: 600,
                  color: 'var(--color-foreground)',
                  outline: 'none',
                  transition: 'border-color 200ms ease-out',
                  caretColor: 'var(--color-accent)',
                }}
                onFocus={e => { if (!error) e.target.style.borderColor = 'var(--color-primary)'; }}
                onBlur={e => { if (!error && !digit) e.target.style.borderColor = 'rgba(156, 163, 175, 0.3)'; }}
              />
            ))}
          </div>
          {error && (
            <p style={{ fontFamily: 'var(--font-caption)', fontSize: '12px', fontWeight: 400, color: '#FF5722', marginTop: '10px', display: 'flex', alignItems: 'center', gap: '4px' }}>
              <span>⚠</span>{error}
            </p>
          )}
          <p style={{ fontFamily: 'var(--font-caption)', fontSize: '12px', color: 'var(--color-muted-foreground)', marginTop: '8px' }}>
            Demo: bruk kode <strong style={{ color: 'var(--color-accent)' }}>123456</strong> (krever 2FA aktivert)
          </p>
        </div>

        <button
          type="submit"
          disabled={isLoading}
          style={{
            width: '100%',
            background: isLoading ? 'rgba(255, 87, 34, 0.6)' : 'var(--color-accent)',
            border: '2px solid var(--color-accent)',
            borderRadius: 'var(--radius-sm)',
            padding: '14px 24px',
            fontFamily: 'var(--font-heading)',
            fontSize: '16px',
            fontWeight: 700,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            color: '#FFFFFF',
            cursor: isLoading ? 'not-allowed' : 'pointer',
            transition: 'all 250ms ease-out',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            marginBottom: '16px',
          }}
          onMouseEnter={e => { if (!isLoading) { e.currentTarget.style.background = '#E64A19'; e.currentTarget.style.borderColor = '#E64A19'; } }}
          onMouseLeave={e => { if (!isLoading) { e.currentTarget.style.background = 'var(--color-accent)'; e.currentTarget.style.borderColor = 'var(--color-accent)'; } }}
        >
          {isLoading ? (
            <>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ animation: 'spin 1s linear infinite' }}>
                <path d="M21 12a9 9 0 11-6.219-8.56" />
              </svg>
              Verifiserer...
            </>
          ) : 'Bekreft kode'}
        </button>
      </form>
      <button
        type="button"
        onClick={onSwitchToEmail}
        style={{
          background: 'transparent',
          border: 'none',
          padding: '8px 0',
          fontFamily: 'var(--font-caption)',
          fontSize: '13px',
          fontWeight: 500,
          color: 'var(--color-primary)',
          cursor: 'pointer',
          transition: 'opacity 150ms ease-out',
          letterSpacing: '0.01em',
        }}
        onMouseEnter={e => e.currentTarget.style.opacity = '0.7'}
        onMouseLeave={e => e.currentTarget.style.opacity = '1'}
      >
        ← Tilbake til e-postverifisering
      </button>
      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

export default BackupCodeEntry;
